import React, { useCallback, useMemo, useState } from 'react';

import {
  AUDIO_MIMETYPE,
  getFileObject,
  IMAGE_MIMETYPE,
  PDF_MIMETYPE,
  TEXT_MIMETYPE,
  VIDEO_MIMETYPE,
} from 'helpers/FileHelpers';
import {
  VideoAppletModalPreview,
} from 'modules/profile/components';
import ImageAppletModalPreviewWithConversion from 'modules/profile/components/ImageAppletModalPreviewWithConversion';
import PdfPreview from 'components/pdf-preview';
import TxtPreview from 'components/applets/app-upload/file-preview/TxtPreview';
import { ArtifactDescription, ArtifactInputsContainer, ArtifactTitle } from 'components/applet-custom-props/components';
import { AppletTextContainer, ArtifactInput, CancelButton, ProgressContainer, SaveButton } from 'components/applets/components';
import { TextArea } from 'components/components';
import { AppletCustomFields } from 'models/Applet';
import { QuestApplet } from 'models/QuestApplet';
import { ChallengeApplet } from 'models/ChallengeApplet';
import { CircularProgress } from '@material-ui/core';
import { ButtonContainer } from 'common/CommonComponents';
import localizedStrings from 'localization/en';
import useActionStatus from 'hooks/useActionStatus';
import { useDispatch } from 'react-redux';
import { AppDispatch } from 'redux/typings';
import { updateCustomAppletProps } from 'modules/applets/actions';
import { RichtTextContainer } from 'components/applets/rich-text/components';
import { AppletContentful } from 'models/AppletContentful';

type FileUploadPreviewProps = {
  applet: QuestApplet | ChallengeApplet;
  appletDef?: AppletContentful;
  disabledApplet?: boolean;
};

const TYPES_AVAILABLES = ['generic-upload-file', 'generic-rich-text'];

const FileUploadPreview = ({ applet, appletDef, disabledApplet = false }: FileUploadPreviewProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const { fileUrl, fileType, title } = applet;
  const [isLoading] = useActionStatus(updateCustomAppletProps);
  const [disabled, setDisabled] = useState(disabledApplet);
  const [customFields, setCustomFields] = useState<AppletCustomFields>({
    customTitle: (applet as QuestApplet)?.customTitle || appletDef?.title,
    customDescription: (applet as QuestApplet)?.customDescription || (applet as QuestApplet)?.description || appletDef?.description,
  });

  const canEdit = TYPES_AVAILABLES.includes(applet.appletType);

  const Preview = useMemo(() => {
    const fileObject = getFileObject({ fileType, fileUrl });
    switch (fileObject?.type) {
      case IMAGE_MIMETYPE:
        return <ImageAppletModalPreviewWithConversion src={fileUrl} alt={title} fileType={fileType} />;
      case VIDEO_MIMETYPE:
        return <VideoAppletModalPreview src={fileUrl} controls autoPlay />;
      case PDF_MIMETYPE:
        return (
          <PdfPreview file={{ uri: fileUrl!, type: 'string', name: 'string' }} />
        );
      case AUDIO_MIMETYPE:
        return <audio src={fileUrl} controls />;
      case TEXT_MIMETYPE:
        return (
          <TxtPreview file={{ uri: fileUrl!, type: 'string', name: 'string' }} />
        );
      default:
        return null;
    }
  }, [fileType, fileUrl, title]);

  const onValueChange = useCallback(
    (
      event: React.ChangeEvent<
        | {
          name?: string | undefined;
          value: unknown;
        }
        | HTMLTextAreaElement
        | HTMLInputElement
      >,
    ) => setCustomFields(prev => ({ ...prev, [event.target.name as string]: event.target.value })), [],
  );

  const handleEdit = () => setDisabled(!disabled);

  const submitChanges = () => {
    setDisabled(true);
    dispatch(updateCustomAppletProps({ id: applet.id, ...customFields }))
  }

  return (<AppletTextContainer>
    <ArtifactInputsContainer>
      <>{canEdit ?
        <>{disabled ?
          <>
            {customFields.customTitle && <ArtifactTitle>{customFields.customTitle}</ArtifactTitle>}
            {customFields.customDescription && <ArtifactDescription>{customFields.customDescription}</ArtifactDescription>}
          </>
          :
          <>
            <ArtifactInput
              name='customTitle'
              value={customFields.customTitle}
              defaultValue={customFields.customTitle}
              onChange={onValueChange}
              placeholder='Title'
            />
            <TextArea
              name='customDescription'
              value={customFields.customDescription}
              defaultValue={customFields.customDescription}
              onChange={onValueChange}
              placeholder='Description'
            />
          </>}
        </>
        :
        <>
          {customFields.customTitle && <ArtifactTitle>{customFields.customTitle}</ArtifactTitle>}
          {customFields.customDescription && <ArtifactDescription>{customFields.customDescription}</ArtifactDescription>}
        </>}</>


      <RichtTextContainer disabled={disabled}>
        {Preview}
      </RichtTextContainer>

      <>{canEdit ?
        <>{isLoading ?
          <ProgressContainer>
            <CircularProgress size={22} color="primary" />
          </ProgressContainer> :
          disabled ?
            <ButtonContainer>
              <SaveButton onClick={handleEdit}>
                {localizedStrings.editContent}
              </SaveButton>
            </ButtonContainer> :
            <ButtonContainer>
              <CancelButton onClick={handleEdit}>
                {localizedStrings.cancel}
              </CancelButton>
              <SaveButton onClick={submitChanges}>
                {localizedStrings.save}
              </SaveButton>
            </ButtonContainer>}
        </> : null}</>

    </ArtifactInputsContainer>
  </AppletTextContainer>)
};

export default FileUploadPreview;
